import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  Divider, Grid2 as Grid, IconButton, InputAdornment, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  consultantsApi, servicesApi, serviceGroupsApi,
  type ConsultantDto, type RevenueShareType, type UpsertConsultantRequest,
  type ServiceDto, type ServiceGroupDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/store/auth";
import { ConsultantPayoutDialog } from "@/features/sales/ConsultantPayoutDialog";
import { todayIso } from "@/shared/datetime";

const today = todayIso;
const pct = (n?: number | null) => (n == null ? "—" : `${n}%`);

/**
 * Consultants and what they earn (W20).
 *
 * <p>The share belongs to the person, not to the price. A consultation billed at one rate is
 * shared differently across twelve consultants, and on the old model — a flat professional charge
 * on the rate row — that meant twelve prices for one consultation, or eleven doctors paid the
 * wrong amount.</p>
 *
 * <p>The rules editor is the screen. It reads most-specific-first, which is the order the resolver
 * applies them in: a maintainer scanning the list sees what will actually happen rather than
 * having to work out which row wins.</p>
 */
export function ConsultantsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [editing, setEditing] = useState<ConsultantDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [payoutFor, setPayoutFor] = useState<ConsultantDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("sales.consultant.manage"));
  const canSeePayout = useAuth(s => s.hasPermission("sales.consultant.payout"));

  const query = useQuery({
    queryKey: ["consultants", search, pagination.page, pagination.pageSize],
    queryFn: () => consultantsApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Consultant deleted" },
    mutationFn: (id: string) => consultantsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consultants"] }),
  });

  const columns: GridColDef<ConsultantDto>[] = [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
    {
      field: "specialty", headerName: "Specialty", width: 160,
      renderCell: (p) => p.row.specialty
        ?? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>,
    },
    {
      field: "ruleCount", headerName: "Share rules", width: 130, align: "right",
      headerAlign: "right",
      renderCell: (p) => p.row.ruleCount === 0
        ? (
          <Tooltip title="No rules — lines fall back to the rate row's flat professional charge">
            <Box component="span" sx={{ color: "text.secondary" }}>flat charge</Box>
          </Tooltip>
        )
        : <span>{p.row.ruleCount}</span>,
    },
    {
      field: "defaultSharePct", headerName: "Default", width: 100, align: "right",
      headerAlign: "right",
      renderCell: (p) => pct(p.row.defaultSharePct),
    },
    {
      field: "healNTrackEmployeeId", headerName: "HealNTrack", width: 120,
      renderCell: (p) => p.row.healNTrackEmployeeId == null
        ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        : (
          <Tooltip title="Orders arriving from this employee attribute to this consultant">
            <Box component="span">#{p.row.healNTrackEmployeeId}</Box>
          </Tooltip>
        ),
    },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => (
        <StatusPill label={p.row.isActive ? "Active" : "Inactive"}
          tone={p.row.isActive ? "success" : "neutral"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          {
            label: "Payout statement", icon: <PaymentsIcon fontSize="small" />,
            hidden: !canSeePayout,
            onClick: () => setPayoutFor(p.row),
          },
          {
            label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage,
            onClick: () => { setEditing(p.row); setFormOpen(true); },
          },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error",
            hidden: !canManage,
            onClick: () => {
              if (confirm(`Delete ${p.row.name}? Somebody who has been billed against cannot be deleted — deactivate them instead.`)) {
                deleteMutation.mutate(p.row.id);
              }
            },
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Sales"]}
        title="Consultants"
        subtitle="Who performs a service, and what they earn for it. The share belongs to the person — one consultation can be shared twelve ways without twelve prices."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setFormOpen(true); }}>
            New consultant
          </Button>
        ) : undefined}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        A consultant with <b>no rules</b> earns whatever the rate row&apos;s flat professional
        charge says — which is what this hospital was paying before. Nothing changes until a rule
        is written, and then the most specific one wins: a service beats its group, which beats a
        blanket arrangement.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code, name or specialty" fullWidth size="small" sx={{ mb: 2 }}
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>
        )}

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && (setEditing(p.row), setFormOpen(true))}
          />
        </Box>
      </Paper>

      <ConsultantDialog open={formOpen} onClose={() => setFormOpen(false)} consultant={editing} />
      <ConsultantPayoutDialog consultant={payoutFor} onClose={() => setPayoutFor(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Create / edit, with the rules
// ---------------------------------------------------------------------------------------------

interface DraftRule {
  key: string;
  /** "Service" | "Group" | "All" — what this rule is aimed at. */
  target: "Service" | "Group" | "All";
  serviceId: string;
  serviceName: string;
  serviceGroupId: string;
  serviceGroupName: string;
  shareType: RevenueShareType;
  value: string;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

const blankRule = (): DraftRule => ({
  key: crypto.randomUUID(), target: "All",
  serviceId: "", serviceName: "", serviceGroupId: "", serviceGroupName: "",
  shareType: "Percent", value: "", effectiveFrom: today(), effectiveTo: "", isActive: true,
});

function ConsultantDialog({
  open, onClose, consultant,
}: { open: boolean; onClose: () => void; consultant: ConsultantDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!consultant;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [registrationNo, setRegistrationNo] = useState("");
  const [phone, setPhone] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [defaultSharePct, setDefaultSharePct] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [rules, setRules] = useState<DraftRule[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(consultant?.code ?? "");
    setName(consultant?.name ?? "");
    setSpecialty(consultant?.specialty ?? "");
    setRegistrationNo(consultant?.registrationNo ?? "");
    setPhone(consultant?.phone ?? "");
    setEmployeeId(consultant?.healNTrackEmployeeId?.toString() ?? "");
    setDefaultSharePct(consultant?.defaultSharePct?.toString() ?? "");
    setIsActive(consultant?.isActive ?? true);
    setRules((consultant?.shareRules ?? []).map(r => ({
      key: crypto.randomUUID(),
      target: r.serviceId ? "Service" : r.serviceGroupId ? "Group" : "All",
      serviceId: r.serviceId ?? "", serviceName: r.serviceName ?? "",
      serviceGroupId: r.serviceGroupId ?? "", serviceGroupName: r.serviceGroupName ?? "",
      shareType: r.shareType, value: String(r.value),
      effectiveFrom: r.effectiveFrom, effectiveTo: r.effectiveTo ?? "",
      isActive: r.isActive,
    })));
  }, [open, consultant]);

  const services = useQuery({
    queryKey: ["services", "share-options"],
    queryFn: () => servicesApi.list({ isActive: true, pageSize: 300 }),
    staleTime: 5 * 60_000, enabled: open,
  });
  const groups = useQuery({
    queryKey: ["service-groups", "share-options"],
    queryFn: () => serviceGroupsApi.list({ pageSize: 200 }),
    staleTime: 5 * 60_000, enabled: open,
  });

  const set = (key: string, patch: Partial<DraftRule>) =>
    setRules(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Consultant updated" : "Consultant created" },
    mutationFn: async () => {
      const payload: UpsertConsultantRequest = {
        code: code.trim(),
        name: name.trim(),
        specialty: specialty.trim() || null,
        registrationNo: registrationNo.trim() || null,
        phone: phone.trim() || null,
        healNTrackEmployeeId: employeeId.trim() ? Number(employeeId) : null,
        defaultSharePct: defaultSharePct.trim() ? Number(defaultSharePct) : null,
        isActive,
        shareRules: rules.map(r => ({
          serviceId: r.target === "Service" ? r.serviceId || null : null,
          serviceGroupId: r.target === "Group" ? r.serviceGroupId || null : null,
          shareType: r.shareType,
          value: Number(r.value || 0),
          effectiveFrom: r.effectiveFrom,
          effectiveTo: r.effectiveTo || null,
          isActive: r.isActive,
        })),
      };
      if (isEdit) await consultantsApi.update(consultant!.id, payload);
      else await consultantsApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["consultants"] });
      onClose();
    },
  });

  const err =
    !code.trim() ? "Code required"
    : !name.trim() ? "Name required"
    : employeeId.trim() && Number.isNaN(Number(employeeId)) ? "HealNTrack employee id must be a number"
    : defaultSharePct.trim() && (Number(defaultSharePct) < 0 || Number(defaultSharePct) > 100)
      ? "A default share must be between 0 and 100 percent"
    : rules.some(r => r.value === "" || Number(r.value) < 0) ? "Every rule needs a value of zero or more"
    : rules.some(r => r.shareType === "Percent" && Number(r.value) > 100)
      ? "A percentage share above 100% would pay out more than the line earned"
    : rules.some(r => r.target === "Service" && !r.serviceId) ? "Pick the service each service rule covers"
    : rules.some(r => r.target === "Group" && !r.serviceGroupId) ? "Pick the group each group rule covers"
    : rules.some(r => r.effectiveTo && r.effectiveTo < r.effectiveFrom) ? "A rule cannot end before it starts"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? `Edit ${consultant!.name}` : "New consultant"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={isEdit}
                helperText={isEdit ? "Cannot be changed" : "e.g. DR-RAO"} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Specialty" size="small" fullWidth
                value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Registration no." size="small" fullWidth
                value={registrationNo} onChange={(e) => setRegistrationNo(e.target.value)}
                helperText="Medical council" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Phone" size="small" fullWidth
                value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="HealNTrack employee id" size="small" fullWidth
                value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                helperText="Attributes their orders automatically" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select label="Status" size="small" fullWidth
                value={isActive ? "1" : "0"} onChange={(e) => setIsActive(e.target.value === "1")}>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">What they earn</Typography>
            <Typography variant="caption" color="text.secondary">
              Most specific first — the order the resolver applies them in
            </Typography>
          </Stack>

          {rules.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              No rules. Every line this consultant performs will earn them the rate row&apos;s flat
              professional charge, which is the arrangement in force before W20.
            </Alert>
          )}

          {rules.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 120 }}>Applies to</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Which</TableCell>
                  <TableCell sx={{ width: 120 }}>As</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Value *</TableCell>
                  <TableCell sx={{ width: 150 }}>From *</TableCell>
                  <TableCell sx={{ width: 150 }}>To</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map(r => (
                  <TableRow key={r.key}>
                    <TableCell>
                      <TextField select size="small" fullWidth value={r.target}
                        onChange={(e) => set(r.key, {
                          target: e.target.value as DraftRule["target"],
                          serviceId: "", serviceGroupId: "",
                        })}>
                        <MenuItem value="Service">One service</MenuItem>
                        <MenuItem value="Group">A group</MenuItem>
                        <MenuItem value="All">Everything</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {r.target === "Service" && (
                        <Autocomplete
                          size="small"
                          options={services.data?.items ?? []}
                          getOptionLabel={(o: ServiceDto) => o.name}
                          value={(services.data?.items ?? []).find(s => s.id === r.serviceId) ?? null}
                          onChange={(_, v) => set(r.key, { serviceId: v?.id ?? "", serviceName: v?.name ?? "" })}
                          loading={services.isLoading}
                          renderInput={(p) => (
                            <TextField {...p} placeholder={r.serviceName || "Search service…"} />
                          )}
                        />
                      )}
                      {r.target === "Group" && (
                        <Autocomplete
                          size="small"
                          options={groups.data?.items ?? []}
                          getOptionLabel={(o: ServiceGroupDto) => o.name}
                          value={(groups.data?.items ?? []).find(g => g.id === r.serviceGroupId) ?? null}
                          onChange={(_, v) =>
                            set(r.key, { serviceGroupId: v?.id ?? "", serviceGroupName: v?.name ?? "" })}
                          loading={groups.isLoading}
                          renderInput={(p) => (
                            <TextField {...p} placeholder={r.serviceGroupName || "Search group…"} />
                          )}
                        />
                      )}
                      {r.target === "All" && (
                        <Typography variant="caption" color="text.secondary">
                          Every service they perform, unless a narrower rule covers it
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField select size="small" fullWidth value={r.shareType}
                        onChange={(e) => set(r.key, { shareType: e.target.value as RevenueShareType })}>
                        <MenuItem value="Percent">% of line</MenuItem>
                        <MenuItem value="Amount">Per unit</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" sx={{ width: 100 }}
                        value={r.value} onChange={(e) => set(r.key, { value: e.target.value })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={r.effectiveFrom}
                        onChange={(e) => set(r.key, { effectiveFrom: e.target.value })} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={r.effectiveTo}
                        onChange={(e) => set(r.key, { effectiveTo: e.target.value })} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"
                        onClick={() => setRules(rs => rs.filter(x => x.key !== r.key))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Button startIcon={<AddIcon />} size="small"
              onClick={() => setRules(rs => [...rs, blankRule()])}>
              Add rule
            </Button>
            <TextField label="Default share (%)" type="number" size="small" sx={{ width: 200 }}
              value={defaultSharePct} onChange={(e) => setDefaultSharePct(e.target.value)}
              inputProps={{ min: 0, max: 100, step: "0.01" }}
              helperText="Recorded for reference; rules decide what is paid" />
          </Stack>

          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
